import * as React from "react"
import { cn } from "cn"

export interface CardProps extends React.ComponentProps<"div"> {
  padding?: "none" | "sm" | "md" | "lg";
  interactive?: boolean;
  tone?: "default" | "sunken" | "accent";
}
const paddingClasses = { none: "p-0", sm: "p-[var(--space-3)]", md: "p-[var(--space-5)]", lg: "p-[var(--space-6)]" };
const toneClasses = { default: "bg-card border-[var(--border-subtle)]", sunken: "bg-secondary border-[var(--border-subtle)]", accent: "bg-[var(--accent-soft)] border-[var(--blue-100)]" };
function Card({ className, padding = "md", interactive = false, tone = "default", ...props }: CardProps) {
  return <div data-slot="card" className={cn(
    "rounded-lg border border-solid text-card-foreground shadow-sm transition-[box-shadow,transform]",
    paddingClasses[padding], toneClasses[tone],
    interactive && "cursor-pointer hover:-translate-y-0.5 hover:shadow-lg", className
  )} {...props} />;
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("leading-none font-semibold", className)}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-6", className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 [.border-t]:pt-6", className)}
      {...props}
    />
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
}
